import nxModule from 'nxModule';
import {ILocationService} from 'angular';
import {NgTableParams} from 'ng-table';
import {setBreadcrumbs} from 'shared/utils/set-breadcrumbs';
import {BranchService} from '../../../service/branch.service';
import {UserCache, UserDetails} from '../../../service/users/user.cache';

import templateUrl from './users-list.template.html';
import {Branch} from "management/BranchTypes";
import {NxIFilterService} from "components/technical/angular-filters";
import {Confirmation} from "shared/common/confirmation.types";
import {CommandAccessChecker, CommandService} from "shared/utils/command/command.types";
import {NxRouteService} from "routes/NxRouteService";
import {Breadcrumbs} from "angular-breadcrumbs";
import Authentication from "shared/utils/authentication";

export interface UserWithAllRoles extends UserDetails {
  allRoles: string
}

export class UsersListComponent {

  private static readonly USERS_PER_PAGE: number = 20;

  private branch?: Branch;
  private branches: Branch[] = [];
  private branchNames: Record<number, string> = {};
  private users: UserWithAllRoles[] = [];
  private tableConfig?: NgTableParams<UserWithAllRoles>;
  private allUsers: UserWithAllRoles[] = [];
  private selectedStatus: null | false | true = null;
  private selectedBranchId: number | null = null;
  private defaultBranchId: number | null = null;
  private searchKeyword: string = '';
  private appliedStatus: null | false | true = null;
  private appliedBranchId: number | null = null;
  private appliedKeyword: string = '';
  private searchSubmitted: boolean = false;
  private canUpdateUsers: boolean = false;
  private canUpdateUserCredentials: boolean = false;
  private canDeleteUsers: boolean = false;
  private detailLink!: string;

  constructor(private readonly $route: NxRouteService,
              private readonly $filter: NxIFilterService,
              private readonly $location: ILocationService,
              private readonly command: CommandService,
              private readonly userCache: UserCache,
              private readonly branchService: BranchService,
              private readonly confirmation: Confirmation,
              private readonly breadcrumbs: Breadcrumbs,
              private readonly authentication: Authentication,
              private readonly commandAccessChecker: CommandAccessChecker) {
  }

  async $onInit(): Promise<void> {
    const allBranches = await this.branchService.toPromise();
    const availableBranchIds = this.authentication.context.branchIds;
    this.branches = allBranches.filter(branch => availableBranchIds.includes(branch.id));
    this.branchNames = Object.fromEntries(this.branches.map(branch => [branch.id, branch.name]));

    this.defaultBranchId = this.resolveDefaultBranchId();
    this.resetCriteria();

    this.detailLink = '/admin/organization/users';

    if (this.branch) {
      setBreadcrumbs(this.breadcrumbs, 'users-list-label', this.branch.name);
    }

    const accessChecker = await this.commandAccessChecker.canExecuteCommandAsync();
    this.canUpdateUsers = accessChecker('UpdateUser');
    this.canUpdateUserCredentials = accessChecker('UpdateUserCredentials');
    /*
     * Row-independent, so it is resolved once instead of per row: the delete action carries no
     * command-access-context, and without one the access check cannot vary between users. Should
     * deleting ever become conditional on the row - a rule such as 'only users of my own branch' -
     * this has to go back to a context-aware check made per user.
     */
    this.canDeleteUsers = accessChecker('DeleteUser');

    /*
     * The users are read up front - a silent request served by an app-wide cache - so that
     * pressing Search filters what is already in memory and renders at once. Nothing is
     * listed until it is pressed; see updateList().
     */
    await this.loadUsers();

    this.tableConfig = new NgTableParams<UserWithAllRoles>({
      page: 1,
      count: UsersListComponent.USERS_PER_PAGE
    }, {
      // No page size selector: raising the page size is how this screen gets locked up again.
      counts: [],
      paginationMinBlocks: 3,
      paginationMaxBlocks: 8,
      dataset: this.users
    });
  }

  /**
   * The branch filter opens on one branch rather than on the whole bank: an operator works
   * in a single branch at a time, and rendering every user of every branch at once is slow
   * enough to lock the page up. 'All branch' stays one selection away.
   *
   * The branch handed over on the route wins - that is the one the create / edit / move
   * screens came back from - then the branch currently being worked in. Anything the
   * operator may not see is ignored, which leaves the unfiltered list.
   */
  private resolveDefaultBranchId(): number | null {
    const routeBranchId = Number(this.$route.current.params.branchId);
    const candidates = [
      this.branch?.id,
      Number.isInteger(routeBranchId) ? routeBranchId : undefined,
      this.authentication.context.branchId
    ];

    return candidates.find(id => this.branches.some(branch => branch.id === id)) ?? null;
  }

  private async loadUsers(): Promise<void> {
    const users: UserDetails[] = await this.userCache.withParam().toPromise();
    const availableBranchIds = this.authentication.context.branchIds;

    this.allUsers = users.filter(u => {
      const isTechnical = u.roles.some(r => r.technical); // Exclude users with technical roles
      const isAvailableBranch = availableBranchIds.includes(u.branchId);
      return !isTechnical && isAvailableBranch;
    }).map(u => this.collectRoles(u));

    this.updateList();
  }

  private collectRoles(user: UserDetails): UserWithAllRoles {
    const allRoles = user.roles
      .map(r => this.$filter('prettyEnum')(r.name))
      .join(', ');

    return {
      ...user,
      allRoles
    };
  }

  /**
   * The list is a search result and nothing else: until Search has been pressed there is
   * no result to show, so the table stays empty on the opening screen and again after
   * every Clear. Once a search has been submitted the rendered rows follow the criteria
   * that search applied, never the ones currently sitting in the filter fields - typing a
   * name or picking a branch leaves the results alone until the operator asks for them,
   * and a refresh triggered by something else - deleting a user, say - cannot apply
   * criteria nobody submitted.
   */
  private updateList(): void {
    this.users = this.searchSubmitted ? this.matchingUsers() : [];

    // Not yet built during the first load, which runs before the table exists.
    if (this.tableConfig) {
      this.tableConfig.page(1);
      this.tableConfig.settings({dataset: this.users});
    }
  }

  private matchingUsers(): UserWithAllRoles[] {
    const keyword = this.appliedKeyword.toLowerCase();

    return this.allUsers.filter(user => {
      const matchesStatus = this.appliedStatus === null || user.enabled === this.appliedStatus;
      const matchesBranch = this.appliedBranchId === null || user.branchId === this.appliedBranchId;
      const matchesSearch = !keyword ||
        user.username.toLowerCase().includes(keyword) ||
        (user.fullName && user.fullName.toLowerCase().includes(keyword));

      return matchesStatus && matchesBranch && matchesSearch;
    });
  }

  private applyCriteria(): void {
    this.appliedKeyword = this.searchKeyword;
    this.appliedBranchId = this.selectedBranchId;
    this.appliedStatus = this.selectedStatus;
  }

  /**
   * Back to the opening state: the filter fields on their defaults and no search
   * outstanding, which is what leaves the table empty.
   */
  private resetCriteria(): void {
    this.searchKeyword = '';
    this.selectedBranchId = this.defaultBranchId;
    this.selectedStatus = null;
    this.applyCriteria();
    this.searchSubmitted = false;
  }

  search(): void {
    this.applyCriteria();
    this.searchSubmitted = true;
    this.updateList();
  }

  clearSearch(): void {
    this.resetCriteria();
    this.updateList();
  }

  canOpenUserDetails(user: UserWithAllRoles): boolean {
    return this.canUpdateUsers || this.canUpdateUserCredentials || user.id === this.authentication.context.id;
  }

  addNew(): void {
    const branchId = this.selectedBranchId || (this.branches.length > 0 ? this.branches[0].id : 0);
    this.$location.path(`${this.detailLink}/${branchId}/user/create`);
  }

  editUser(user: UserWithAllRoles): void {
    this.$location.path(`${this.detailLink}/${user.branchId}/user/${user.id}`);
  }

  async deleteUser(id: number): Promise<void> {
    const confirmed = await this.confirmation('Are you sure you want to delete this user?' +
      ' Please note that this action cannot be reversed and the username cannot be used again ' +
      'as it must stay in the system for audit purposes.');
    if (confirmed) {
      /*
       * Steps to perform one by one:
       * - delete user
       * - clear users cache
       * - load users
       */
      const response = await this.command.execute('DeleteUser', {id: id}).toPromise();
      if(response.approvalRequired) {
        return;
      }

      await this.userCache.withParam().refetch();
      await this.loadUsers();
    }
  }
}

nxModule.component('usersList', {
  templateUrl,
  bindings: {
    branch: '<'
  },
  controller: UsersListComponent
});
